import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import {
  listPricingLabels,
  listRuleSets,
  listSeasonalRules,
  createSeasonalRule,
  updateSeasonalRule,
  deleteSeasonalRule,
  toggleSeasonalRule,
  listOfferRules,
  createOfferRule,
  updateOfferRule,
  deleteOfferRule,
  toggleOfferRule,
  listTaxFeeRules,
  createTaxFeeRule,
  updateTaxFeeRule,
  deleteTaxFeeRule,
  toggleTaxFeeRule,
  previewTaxFeeQuote,
  previewQuote,
} from '../../api/adminPricing';
import { apiGet } from '../../services/apiClient';

const emptyForms = {
  seasonal: {
    rule_set_id: '',
    label_id: '',
    scope: 'property',
    property_id: '',
    host_id: '',
    property_type: '',
    start_date: '',
    end_date: '',
    days_of_week: [],
    price_mode: 'set',
    value: '',
    min_nights: 1,
    priority: 100,
    is_active: true,
  },
  offers: {
    rule_set_id: '',
    label_id: '',
    scope: 'property',
    property_id: '',
    host_id: '',
    property_type: '',
    start_date: '',
    end_date: '',
    discount_type: 'percent',
    discount_value: 5,
    min_nights: 1,
    max_uses_total: '',
    max_uses_per_guest: '',
    stack_rule: 'none',
    priority: 100,
    is_active: true,
  },
  taxes: {
    rule_set_id: '',
    label_id: '',
    scope: 'platform',
    region: '',
    property_type: '',
    property_id: '',
    charge_type: 'tax',
    name: '',
    calc_type: 'percent',
    value: '',
    applies_to: 'base',
    is_included_in_price: false,
    is_refundable: true,
    priority: 100,
    is_active: true,
  },
};

const configs = {
  seasonal: {
    title: 'Seasonal pricing',
    subtitle: 'Create seasonal rates and day-of-week uplifts that cascade into every quote.',
    list: listSeasonalRules,
    create: createSeasonalRule,
    update: updateSeasonalRule,
    remove: deleteSeasonalRule,
    toggle: toggleSeasonalRule,
    columns: ['Label', 'Scope', 'Date range', 'Rule', 'Min nights', 'Priority', 'Active'],
  },
  offers: {
    title: 'Special offers',
    subtitle: 'Automatic discounts that apply without codes (e.g., long stays).',
    list: listOfferRules,
    create: createOfferRule,
    update: updateOfferRule,
    remove: deleteOfferRule,
    toggle: toggleOfferRule,
    columns: ['Label', 'Scope', 'Date range', 'Discount', 'Min nights', 'Priority', 'Active'],
  },
  taxes: {
    title: 'Taxes & Fees',
    subtitle: 'Platform or property scoped taxes/fees applied at checkout.',
    list: listTaxFeeRules,
    create: createTaxFeeRule,
    update: updateTaxFeeRule,
    remove: deleteTaxFeeRule,
    toggle: toggleTaxFeeRule,
    columns: ['Name/Label', 'Scope', 'Type', 'Calc', 'Applies to', 'Priority', 'Active'],
  },
};

const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const Field = ({ label, children }) => (
  <label className="text-sm text-slate-700 space-y-1">
    <span className="font-semibold">{label}</span>
    {children}
  </label>
);

const HelperNote = ({ children }) => (
  <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">{children}</div>
);

const AdminPricingRules = ({ mode }) => {
  const config = configs[mode];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForms[mode]);
  const [editingId, setEditingId] = useState(null);
  const [filters, setFilters] = useState({ scope: '', active: 'true', property_id: '', q: '' });
  const [labels, setLabels] = useState([]);
  const [ruleSets, setRuleSets] = useState([]);
  const [properties, setProperties] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [previewParams, setPreviewParams] = useState({ property_id: '', check_in: '', check_out: '', guests_count: 2 });
  const [preview, setPreview] = useState({ loading: false, data: null, error: '' });

  const loadMeta = async () => {
    try {
      const [lbl, rs, props] = await Promise.all([
        listPricingLabels(),
        listRuleSets(),
        apiGet('/api/admin/properties?limit=200'),
      ]);
      setLabels(asArray(lbl));
      setRuleSets(asArray(rs));
      setProperties(asArray(props));
    } catch (_) {
      // ignore
    }
  };

  const loadRules = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await config.list(filters);
      setItems(asArray(data));
    } catch (err) {
      setError(err.message || 'Failed to load rules');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
  }, []);

  useEffect(() => {
    loadRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, filters.scope, filters.active, filters.property_id, filters.q]);

  const resetForm = () => {
    setForm(emptyForms[mode]);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    const payload = { ...form };
    try {
      const saved = editingId ? await config.update(editingId, payload) : await config.create(payload);
      const next = items.filter((i) => i.id !== (saved?.id || editingId));
      setItems([saved, ...next]);
      resetForm();
    } catch (err) {
      setError(err.message || 'Failed to save rule');
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      ...emptyForms[mode],
      ...row,
      property_id: row.property_id || '',
      host_id: row.host_id || '',
      property_type: row.property_type || '',
      rule_set_id: row.rule_set_id || '',
      label_id: row.label_id || '',
    });
    setShowForm(true);
  };

  const toggleActive = async (row) => {
    try {
      const updated = await config.toggle(row.id, !row.is_active);
      setItems((prev) => prev.map((i) => (i.id === row.id ? updated : i)));
    } catch (err) {
      setError(err.message || 'Failed to toggle');
    }
  };

  const removeRule = async (row) => {
    if (!window.confirm('Delete this rule?')) return;
    await config.remove(row.id);
    setItems((prev) => prev.filter((i) => i.id !== row.id));
  };

  const handlePreview = async () => {
    setPreview({ loading: true, data: null, error: '' });
    try {
      const data = await previewQuote({
        property_id: previewParams.property_id,
        check_in: previewParams.check_in,
        check_out: previewParams.check_out,
        guests_count: previewParams.guests_count || 2,
      });
      setPreview({ loading: false, data, error: '' });
    } catch (err) {
      setPreview({ loading: false, data: null, error: err.message || 'Failed to fetch quote' });
    }
  };

  const renderRuleSummary = (row) => {
    if (mode === 'seasonal') {
      return `${row.price_mode} ${row.value}`;
    }
    if (mode === 'offers') {
      return `${row.discount_type} ${row.discount_value}${row.stack_rule ? ` · ${row.stack_rule}` : ''}`;
    }
    return `${row.charge_type} · ${row.calc_type} ${row.value}`;
  };

  const propertyOptions = useMemo(() => properties.map((p) => ({ value: p.id, label: `${p.id} · ${p.title || p.name}` })), [properties]);

  const renderField = (key, label) => {
    if (key === 'scope') {
      const scopes = mode === 'taxes' ? ['platform', 'region', 'property_type', 'property'] : ['platform', 'property_type', 'property', 'host'];
      return (
        <Field label={label}>
          <select
            value={form.scope}
            onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
          >
            {scopes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
      );
    }
    if (key === 'rule_set_id') {
      return (
        <Field label="Rule set">
          <select
            value={form.rule_set_id}
            onChange={(e) => setForm((f) => ({ ...f, rule_set_id: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
          >
            <option value="">None</option>
            {ruleSets.map((rs) => (
              <option key={rs.id} value={rs.id}>
                {rs.name}
              </option>
            ))}
          </select>
        </Field>
      );
    }
    if (key === 'label_id') {
      return (
        <Field label="Label">
          <select
            value={form.label_id}
            onChange={(e) => setForm((f) => ({ ...f, label_id: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
          >
            <option value="">None</option>
            {labels.map((lbl) => (
              <option key={lbl.id} value={lbl.id}>
                {lbl.name}
              </option>
            ))}
          </select>
        </Field>
      );
    }
    if (key === 'property_id') {
      return (
        <Field label="Property">
          <select
            value={form.property_id}
            onChange={(e) => setForm((f) => ({ ...f, property_id: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
          >
            <option value="">All</option>
            {propertyOptions.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </Field>
      );
    }
    if (key === 'days_of_week') {
      return (
        <Field label="Days of week">
          <div className="flex flex-wrap gap-2">
            {dayLabels.map((d, idx) => (
              <button
                key={d}
                type="button"
                onClick={() =>
                  setForm((f) => {
                    const has = f.days_of_week?.includes(idx);
                    const next = has ? f.days_of_week.filter((v) => v !== idx) : [...(f.days_of_week || []), idx];
                    return { ...f, days_of_week: next };
                  })
                }
                className={`rounded-lg border px-2 py-1 text-xs ${
                  form.days_of_week?.includes(idx) ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-700'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </Field>
      );
    }
    if (key === 'price_mode') {
      return (
        <Field label="Price mode">
          <select
            value={form.price_mode}
            onChange={(e) => setForm((f) => ({ ...f, price_mode: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
          >
            <option value="set">Set</option>
            <option value="add">Add</option>
            <option value="percent">Percent</option>
          </select>
        </Field>
      );
    }
    if (key === 'discount_type') {
      return (
        <Field label="Discount type">
          <select
            value={form.discount_type}
            onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
          >
            <option value="percent">Percent</option>
            <option value="fixed">Fixed</option>
          </select>
        </Field>
      );
    }
    if (key === 'stack_rule') {
      return (
        <Field label="Stacking">
          <select
            value={form.stack_rule}
            onChange={(e) => setForm((f) => ({ ...f, stack_rule: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
          >
            <option value="none">No stacking</option>
            <option value="allow_with_coupons">Allow with coupons</option>
            <option value="allow_with_promotions">Allow with promotions</option>
            <option value="allow_all">Allow all</option>
          </select>
        </Field>
      );
    }
    if (key === 'charge_type') {
      return (
        <Field label="Charge type">
          <select
            value={form.charge_type}
            onChange={(e) => setForm((f) => ({ ...f, charge_type: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
          >
            <option value="tax">Tax</option>
            <option value="fee">Fee</option>
          </select>
        </Field>
      );
    }
    if (key === 'calc_type') {
      return (
        <Field label="Calc type">
          <select
            value={form.calc_type}
            onChange={(e) => setForm((f) => ({ ...f, calc_type: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
          >
            <option value="percent">Percent</option>
            <option value="fixed">Fixed</option>
            <option value="per_night">Per night</option>
            <option value="per_guest">Per guest</option>
            <option value="per_booking">Per booking</option>
          </select>
        </Field>
      );
    }
    if (key === 'applies_to') {
      return (
        <Field label="Applies to">
          <select
            value={form.applies_to}
            onChange={(e) => setForm((f) => ({ ...f, applies_to: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
          >
            <option value="base">Base</option>
            <option value="base_plus_fees">Base + fees</option>
            <option value="total">Total</option>
          </select>
        </Field>
      );
    }
    if (typeof form[key] === 'boolean') {
      return (
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={!!form[key]}
            onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))}
          />
          <span className="font-semibold">{label}</span>
        </label>
      );
    }
    const type = ['start_date', 'end_date'].includes(key) ? 'date' : ['priority', 'value', 'discount_value', 'min_nights'].includes(key) ? 'number' : 'text';
    return (
      <Field label={label}>
        <input
          type={type}
          value={form[key] ?? ''}
          onChange={(e) => setForm((f) => ({ ...f, [key]: type === 'number' ? Number(e.target.value) : e.target.value }))}
          className="w-full rounded-lg border border-slate-200 px-3 py-2"
        />
      </Field>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="admin-page-title text-2xl font-semibold text-slate-900">{config.title}</h1>
          <p className="text-sm text-slate-600">{config.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-white shadow-sm hover:bg-emerald-500"
          >
            Create rule
          </button>
          {loading && <span className="text-xs text-slate-500">Loading...</span>}
        </div>
      </div>

      {error && <div className="rounded-md bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>}

      <div className="admin-card rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Scope">
            <select
              value={filters.scope}
              onChange={(e) => setFilters((f) => ({ ...f, scope: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
            >
              <option value="">All</option>
              <option value="platform">Platform</option>
              <option value="property">Property</option>
              <option value="property_type">Property type</option>
              {mode !== 'taxes' && <option value="host">Host</option>}
              {mode === 'taxes' && <option value="region">Region</option>}
            </select>
          </Field>
          <Field label="Property">
            <select
              value={filters.property_id}
              onChange={(e) => setFilters((f) => ({ ...f, property_id: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
            >
              <option value="">All</option>
              {propertyOptions.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Active">
            <select
              value={filters.active}
              onChange={(e) => setFilters((f) => ({ ...f, active: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </Field>
          <Field label="Search label/name">
            <input
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              placeholder="Search label"
            />
          </Field>
        </div>
      </div>

      <div className="admin-card rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="font-semibold text-slate-800">Rules</div>
          <button
            type="button"
            onClick={handlePreview}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50"
          >
            Rule Preview
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                {config.columns.map((col) => (
                  <th key={col} className="px-4 py-2 text-left font-semibold text-slate-700">
                    {col}
                  </th>
                ))}
                <th className="px-4 py-2 text-right font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((row) => (
                <tr key={row.id}>
                  {mode === 'taxes' ? (
                    <>
                      <td className="px-4 py-2">
                        <div className="font-semibold text-slate-800">{row.label_name || row.name || '-'}</div>
                        {row.label_color && <div className="text-xs text-slate-500">#{row.label_color}</div>}
                      </td>
                      <td className="px-4 py-2 capitalize text-slate-700">{row.scope || '-'}</td>
                      <td className="px-4 py-2 text-slate-700 capitalize">{row.charge_type}</td>
                      <td className="px-4 py-2 text-slate-700">
                        {row.calc_type} {row.value}
                      </td>
                      <td className="px-4 py-2 text-slate-700">{row.applies_to}</td>
                      <td className="px-4 py-2 text-slate-700">{row.priority}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`rounded-full px-3 py-1 text-xs ${
                            row.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {row.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-2">
                        <div className="font-semibold text-slate-800">{row.label_name || row.name || row.label || '-'}</div>
                        {row.label_color && <div className="text-xs text-slate-500">#{row.label_color}</div>}
                      </td>
                      <td className="px-4 py-2 capitalize text-slate-700">{row.scope || '-'}</td>
                      <td className="px-4 py-2 text-slate-700">
                        {row.start_date || '—'} → {row.end_date || '—'}
                      </td>
                      <td className="px-4 py-2 text-slate-700">{renderRuleSummary(row)}</td>
                      <td className="px-4 py-2 text-slate-700">{row.min_nights || '-'}</td>
                      <td className="px-4 py-2 text-slate-700">{row.priority}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`rounded-full px-3 py-1 text-xs ${
                            row.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {row.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </>
                  )}
                  <td className="px-4 py-2 text-right space-x-2">
                    <button
                      type="button"
                      onClick={() => toggleActive(row)}
                      className="text-xs text-emerald-700 hover:underline"
                    >
                      {row.is_active ? 'Pause' : 'Activate'}
                    </button>
                    <button type="button" onClick={() => startEdit(row)} className="text-xs text-slate-700 hover:underline">
                      Edit
                    </button>
                    <button type="button" onClick={() => removeRule(row)} className="text-xs text-red-600 hover:underline">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td colSpan={config.columns.length + 1} className="px-4 py-6 text-center text-slate-500">
                    No rules yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">{editingId ? 'Edit rule' : 'Create rule'}</h2>
                <p className="text-sm text-slate-600">Fill out the fields below and save.</p>
              </div>
              <button type="button" onClick={resetForm} className="text-sm text-slate-500 hover:text-slate-700">
                Close
              </button>
            </div>
            <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {mode === 'seasonal' && (
                <>
                  {renderField('rule_set_id', 'Rule set')}
                  {renderField('label_id', 'Label')}
                  {renderField('scope', 'Scope')}
                  {renderField('property_id', 'Property (optional)')}
                  {renderField('property_type', 'Property type')}
                  {renderField('start_date', 'Start date')}
                  {renderField('end_date', 'End date')}
                  {renderField('days_of_week', 'Days of week')}
                  {renderField('price_mode', 'Price mode')}
                  {renderField('value', 'Value')}
                  {renderField('min_nights', 'Min nights')}
                  {renderField('priority', 'Priority')}
                  {renderField('is_active', 'Active')}
                </>
              )}
              {mode === 'offers' && (
                <>
                  {renderField('rule_set_id', 'Rule set')}
                  {renderField('label_id', 'Label')}
                  {renderField('scope', 'Scope')}
                  {renderField('property_id', 'Property (optional)')}
                  {renderField('property_type', 'Property type')}
                  {renderField('start_date', 'Start date')}
                  {renderField('end_date', 'End date')}
                  {renderField('discount_type', 'Discount type')}
                  {renderField('discount_value', 'Discount value')}
                  {renderField('min_nights', 'Min nights')}
                  {renderField('max_uses_total', 'Max uses total')}
                  {renderField('max_uses_per_guest', 'Max uses per guest')}
                  {renderField('stack_rule', 'Stacking')}
                  {renderField('priority', 'Priority')}
                  {renderField('is_active', 'Active')}
                </>
              )}
              {mode === 'taxes' && (
                <>
                  {renderField('rule_set_id', 'Rule set')}
                  {renderField('label_id', 'Label')}
                  {renderField('scope', 'Scope')}
                  {renderField('region', 'Region')}
                  {renderField('property_type', 'Property type')}
                  {renderField('property_id', 'Property (optional)')}
                  {renderField('charge_type', 'Charge type')}
                  {renderField('name', 'Name')}
                  {renderField('calc_type', 'Calc type')}
                  {renderField('value', 'Value')}
                  {renderField('applies_to', 'Applies to')}
                  {renderField('is_included_in_price', 'Included in price')}
                  {renderField('is_refundable', 'Refundable')}
                  {renderField('priority', 'Priority')}
                  {renderField('is_active', 'Active')}
                </>
              )}
              <div className="md:col-span-2 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-white shadow-sm hover:bg-emerald-500"
                >
                  Save rule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admin-card rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-800">Rule Preview</div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="Property">
              <select
                value={previewParams.property_id}
                onChange={(e) => setPreviewParams((p) => ({ ...p, property_id: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              >
                <option value="">Select</option>
                {propertyOptions.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Check-in">
              <input
                type="date"
                value={previewParams.check_in}
                onChange={(e) => setPreviewParams((p) => ({ ...p, check_in: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </Field>
            <Field label="Check-out">
              <input
                type="date"
                value={previewParams.check_out}
                onChange={(e) => setPreviewParams((p) => ({ ...p, check_out: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </Field>
            <Field label="Guests">
              <input
                type="number"
                min="1"
                value={previewParams.guests_count}
                onChange={(e) => setPreviewParams((p) => ({ ...p, guests_count: Number(e.target.value) }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </Field>
          </div>
          <button
            type="button"
            onClick={handlePreview}
            className="rounded-lg bg-slate-900 px-4 py-2 text-white hover:bg-slate-800"
          >
            Preview quote
          </button>
          {preview.loading && <div className="text-sm text-slate-500">Loading preview...</div>}
          {preview.error && <div className="text-sm text-red-600">{preview.error}</div>}
          {preview.data && (
            <div className="rounded-lg border border-slate-200 p-4 space-y-2">
              <div className="font-semibold text-slate-800">Totals</div>
              <div className="grid grid-cols-2 gap-2 text-sm text-slate-700">
                <div>Base Price</div>
                <div className="text-right font-semibold">₹{preview.data.base_price ?? preview.data.base_nights_amount}</div>
                <div>Discount</div>
                <div className="text-right font-semibold text-emerald-700">-₹{preview.data.discount_amount ?? preview.data.total_discount}</div>
                <div>Taxable Subtotal</div>
                <div className="text-right font-semibold">₹{preview.data.taxable_subtotal ?? 0}</div>
                <div>GST</div>
                <div className="text-right font-semibold">₹{preview.data.gst_amount ?? preview.data.taxes_total}</div>
                <div>Mandatory fees</div>
                <div className="text-right font-semibold">₹{preview.data.mandatory_fees ?? preview.data.fees_total}</div>
                <div>Total payable</div>
                <div className="text-right font-semibold text-slate-900">₹{preview.data.final_payable ?? preview.data.total_payable}</div>
              </div>
              <div className="text-xs text-slate-500">
                Applied rules: {(preview.data.applied_rules || []).map((r) => `${r.type}#${r.id}`).join(', ') || 'None'}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const taxFeeFormDefaults = {
  rule_name: '',
  display_name: '',
  scope_type: 'global',
  property_id: '',
  host_id: '',
  property_type: '',
  region: '',
  provider_code: '',
  provider_property_id: '',
  charge_type: 'tax_percentage',
  value: '',
  applies_on: 'base_rent',
  calculation_target: 'customer',
  status: true,
  sort_order: 0,
};

const taxFeeChargeLabels = {
  tax_percentage: 'Tax percentage',
  fixed: 'Fixed fee',
  per_night: 'Per night fee',
  per_guest: 'Per guest fee',
  cleaning: 'Cleaning fee',
  service: 'Service fee',
  margin_percent: 'Margin / markup %',
};

const taxFeeAppliesLabels = {
  base_rent: 'Base rent',
  subtotal: 'Subtotal',
  booking_total: 'Booking total',
};

const formatCurrency = (value, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const normalizeTaxFeeRuleForForm = (rule = {}) => ({
  rule_name: rule.rule_name || rule.ruleName || '',
  display_name: rule.display_name || rule.displayName || rule.name || '',
  scope_type: rule.scope_type || (rule.scope === 'property' ? 'property' : 'global'),
  property_id: rule.property_id ? String(rule.property_id) : '',
  host_id: rule.host_id ? String(rule.host_id) : '',
  property_type: rule.property_type || '',
  region: rule.region || '',
  provider_code: rule.provider_code || '',
  provider_property_id: rule.provider_property_id || '',
  charge_type: rule.charge_type || 'tax_percentage',
  value: rule.value !== undefined && rule.value !== null ? String(rule.value) : '',
  applies_on: rule.applies_on || 'base_rent',
  calculation_target: rule.calculation_target || 'customer',
  status: rule.status !== undefined ? Boolean(rule.status) : Boolean(rule.is_active ?? true),
  sort_order: Number(rule.sort_order ?? rule.priority ?? 0) || 0,
});

const renderRuleScopeLabel = (rule = {}, propertyOptions = []) => {
  if (rule.scope_type === 'property') {
    return {
      title: 'Selected property',
      subtitle:
        propertyOptions.find((property) => String(property.id) === String(rule.property_id))?.title ||
        `Property ${rule.property_id || ''}`.trim(),
    };
  }
  if (rule.scope_type === 'host') {
    return { title: 'Host', subtitle: rule.host_id ? `Host ${rule.host_id}` : 'Selected host' };
  }
  if (rule.scope_type === 'property_type') {
    return { title: 'Property type', subtitle: rule.property_type || 'Selected type' };
  }
  if (rule.scope_type === 'region') {
    return { title: 'Region', subtitle: rule.region || 'Selected region' };
  }
  if (rule.scope_type === 'provider') {
    return { title: 'Provider', subtitle: rule.provider_code || 'Selected provider' };
  }
  if (rule.scope_type === 'provider_property') {
    return {
      title: 'Provider property',
      subtitle:
        [rule.provider_code || '', rule.provider_property_id || ''].filter(Boolean).join(' · ') || 'Selected provider property',
    };
  }
  return { title: 'Global', subtitle: 'All properties' };
};

const AdminTaxesFeeManager = () => {
  const [rules, setRules] = useState([]);
  const [properties, setProperties] = useState([]);
  const [filters, setFilters] = useState({ scope: '', property_id: '', provider_code: '', active: 'true', q: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState(taxFeeFormDefaults);
  const [previewParams, setPreviewParams] = useState({
    property_id: '',
    provider_code: '',
    provider_property_id: '',
    check_in: '',
    check_out: '',
    guests_count: 2,
  });
  const [preview, setPreview] = useState({ loading: false, data: null, error: '' });

  const propertyOptions = useMemo(() => {
    const rows = asArray(properties);
    return rows.map((property) => ({
      id: property.id,
      title: property.title || property.name || `Property ${property.id}`,
    }));
  }, [properties]);

  const marginRules = useMemo(
    () => rules.filter((rule) => String(rule.charge_type || '').trim() === 'margin_percent'),
    [rules]
  );

  const marginSummary = useMemo(() => {
    const activeRules = marginRules.filter((rule) => Boolean(rule.status ?? rule.is_active));
    return {
      total: marginRules.length,
      active: activeRules.length,
      property: activeRules.filter((rule) => rule.scope_type === 'property').length,
      provider: activeRules.filter((rule) => ['provider', 'provider_property'].includes(rule.scope_type)).length,
    };
  }, [marginRules]);

  const visibleRules = useMemo(() => {
    const query = String(filters.q || '').trim().toLowerCase();
    const providerCode = String(filters.provider_code || '').trim().toLowerCase();
    return rules.filter((rule) => {
      const isActive = Boolean(rule.status ?? rule.is_active);
      if (filters.scope && rule.scope_type !== filters.scope) return false;
      if (filters.property_id && String(rule.property_id || '') !== String(filters.property_id)) return false;
      if (providerCode && String(rule.provider_code || '').trim().toLowerCase() !== providerCode) return false;
      if (filters.active === 'true' && !isActive) return false;
      if (filters.active === 'false' && isActive) return false;
      if (query) {
        const haystack = `${rule.rule_name || ''} ${rule.display_name || ''}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [filters, rules]);

  const loadMeta = async () => {
    const props = await apiGet('/api/admin/properties?limit=300');
    setProperties(props);
    const firstPropertyId = asArray(props)?.[0]?.id || '';
    setPreviewParams((prev) => ({ ...prev, property_id: prev.property_id || String(firstPropertyId || '') }));
  };

  const loadRules = async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await listTaxFeeRules(filters);
      setRules(asArray(rows));
    } catch (err) {
      setError(err.message || 'Failed to load tax and fee rules.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([loadMeta(), loadRules()]).catch((err) => {
      setError(err.message || 'Failed to load taxes and fees.');
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.scope, filters.property_id, filters.provider_code, filters.active, filters.q]);

  const openCreateWithPreset = (preset = {}) => {
    setEditingRule(null);
    setForm({
      ...taxFeeFormDefaults,
      ...preset,
      status: preset.status ?? true,
      sort_order: preset.sort_order ?? taxFeeFormDefaults.sort_order,
    });
    setDrawerOpen(true);
  };

  const openCreate = () => {
    openCreateWithPreset();
  };

  const openMarginPreset = (scopeType) => {
    openCreateWithPreset({
      scope_type: scopeType,
      property_id: scopeType === 'property' ? filters.property_id || previewParams.property_id || '' : '',
      provider_code:
        scopeType === 'provider' || scopeType === 'provider_property'
          ? filters.provider_code || previewParams.provider_code || ''
          : '',
      provider_property_id: scopeType === 'provider_property' ? previewParams.provider_property_id || '' : '',
      charge_type: 'margin_percent',
      applies_on: 'base_rent',
      calculation_target: 'customer',
    });
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setForm(normalizeTaxFeeRuleForForm(rule));
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingRule(null);
    setForm(taxFeeFormDefaults);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        rule_name: form.rule_name.trim(),
        display_name: (form.display_name || form.rule_name).trim(),
        scope_type: form.scope_type,
        property_id: form.scope_type === 'property' ? Number(form.property_id) : null,
        host_id: form.scope_type === 'host' ? Number(form.host_id) : null,
        property_type: form.scope_type === 'property_type' ? form.property_type.trim() : null,
        region: form.scope_type === 'region' ? form.region.trim() : null,
        provider_code: ['provider', 'provider_property'].includes(form.scope_type) ? form.provider_code.trim().toLowerCase() : null,
        provider_property_id: form.scope_type === 'provider_property' ? form.provider_property_id.trim() : null,
        charge_type: form.charge_type,
        value: Number(form.value),
        applies_on: form.applies_on,
        calculation_target: form.calculation_target,
        status: Boolean(form.status),
        sort_order: Number(form.sort_order || 0),
      };
      if (editingRule?.id) {
        await updateTaxFeeRule(editingRule.id, payload);
      } else {
        await createTaxFeeRule(payload);
      }
      closeDrawer();
      await loadRules();
    } catch (err) {
      setError(err.message || 'Failed to save rule.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (rule) => {
    try {
      await toggleTaxFeeRule(rule.id, !(rule.status ?? rule.is_active));
      await loadRules();
    } catch (err) {
      setError(err.message || 'Failed to update rule status.');
    }
  };

  const handleDelete = async (rule) => {
    const confirmed = window.confirm(`Delete "${rule.display_name}"?`);
    if (!confirmed) return;
    try {
      await deleteTaxFeeRule(rule.id);
      await loadRules();
    } catch (err) {
      setError(err.message || 'Failed to delete rule.');
    }
  };

  const handlePreview = async () => {
    setPreview({ loading: true, data: null, error: '' });
    try {
      const data = await previewTaxFeeQuote({
        property_id:
          previewParams.provider_code && previewParams.provider_property_id
            ? null
            : Number(previewParams.property_id),
        provider_code: previewParams.provider_code.trim().toLowerCase() || null,
        provider_property_id: previewParams.provider_property_id.trim() || null,
        check_in: previewParams.check_in,
        check_out: previewParams.check_out,
        guests_count: Number(previewParams.guests_count || 1) || 1,
      });
      setPreview({ loading: false, data, error: '' });
    } catch (err) {
      setPreview({ loading: false, data: null, error: err.message || 'Failed to preview quote.' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-semibold text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Taxes & Fees
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Create dynamic checkout rules scoped globally or to selected properties.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
        >
          Create Rule
        </button>
      </div>

      {error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      ) : null}

      <HelperNote>
        <div className="font-semibold">Elivaas margin location</div>
        <div className="mt-1">
          For a direct Homevilas property, use <span className="font-semibold">Scope = Selected properties</span>, choose the
          property, then set <span className="font-semibold">Charge type = Margin / markup %</span>.
        </div>
        <div className="mt-1">
          Go to <span className="font-semibold">Create Rule</span>, set <span className="font-semibold">Scope</span> to{' '}
          <span className="font-semibold">Provider</span>, enter <span className="font-semibold">Provider code = elivaas</span>,
          choose <span className="font-semibold">Charge type = Margin / markup %</span>, then enter your percentage like{' '}
          <span className="font-semibold">15</span> or <span className="font-semibold">20</span>.
        </div>
        <div className="mt-1">
          For only one Elivaas property, use <span className="font-semibold">Scope = Provider property</span> and enter that
          provider property ID.
        </div>
        <div className="mt-1">
          You can keep multiple active margin rules across property, provider, and provider-property scopes. The quote engine
          applies only the most specific active margin rule, then the lowest sort order.
        </div>
      </HelperNote>

      <div className="grid gap-4 xl:grid-cols-[1.15fr_1fr_1fr]">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Margin visibility</h2>
              <p className="mt-1 text-sm text-slate-500">
                Active margin rules are applied dynamically in quote preview and checkout.
              </p>
            </div>
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              {marginSummary.active} active
            </span>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">All margin rules</div>
              <div className="mt-2 text-2xl font-semibold text-slate-900">{marginSummary.total}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">Direct property</div>
              <div className="mt-2 text-2xl font-semibold text-slate-900">{marginSummary.property}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">Provider scoped</div>
              <div className="mt-2 text-2xl font-semibold text-slate-900">{marginSummary.provider}</div>
            </div>
          </div>
          <div className="mt-4 text-xs text-slate-500">
            Matching order is <span className="font-semibold text-slate-700">provider property</span>, then{' '}
            <span className="font-semibold text-slate-700">selected property</span>, then{' '}
            <span className="font-semibold text-slate-700">provider</span>, then broader defaults.
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-base font-semibold text-slate-900">Quick margin actions</h2>
          <p className="mt-1 text-sm text-slate-500">Create a margin rule with the correct scope preselected.</p>
          <div className="mt-5 grid gap-3">
            <button
              type="button"
              onClick={() => openMarginPreset('property')}
              className="rounded-2xl border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-slate-800 transition hover:border-emerald-300 hover:bg-emerald-50"
            >
              Add property margin
              <div className="mt-1 text-xs font-normal text-slate-500">Use for one Homevilas property at a custom margin %.</div>
            </button>
            <button
              type="button"
              onClick={() => openMarginPreset('provider')}
              className="rounded-2xl border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-slate-800 transition hover:border-emerald-300 hover:bg-emerald-50"
            >
              Add provider margin
              <div className="mt-1 text-xs font-normal text-slate-500">Set the default margin % for all inventory from one provider.</div>
            </button>
            <button
              type="button"
              onClick={() => openMarginPreset('provider_property')}
              className="rounded-2xl border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-slate-800 transition hover:border-emerald-300 hover:bg-emerald-50"
            >
              Add provider-property margin
              <div className="mt-1 text-xs font-normal text-slate-500">Override the default provider margin for one provider property.</div>
            </button>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-base font-semibold text-slate-900">Linked admin tools</h2>
          <p className="mt-1 text-sm text-slate-500">Jump directly to taxi and payment management from this workspace.</p>
          <div className="mt-5 grid gap-3">
            <Link
              to="/admin/cabs"
              className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 transition hover:border-slate-300 hover:bg-slate-50"
            >
              Taxi dashboard
            </Link>
            <Link
              to="/admin/cabs/pricing-rules"
              className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 transition hover:border-slate-300 hover:bg-slate-50"
            >
              Taxi pricing rules
            </Link>
            <Link
              to="/admin/cabs/vehicles"
              className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 transition hover:border-slate-300 hover:bg-slate-50"
            >
              Taxi vehicles
            </Link>
            <Link
              to="/admin/cabs/enquiries"
              className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 transition hover:border-slate-300 hover:bg-slate-50"
            >
              Taxi enquiries
            </Link>
            <Link
              to="/admin/pricing/partial-payment"
              className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 transition hover:border-slate-300 hover:bg-slate-50"
            >
              Partial payment controls
            </Link>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="grid gap-4 xl:grid-cols-5">
          <Field label="Scope">
            <select
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
              value={filters.scope}
              onChange={(event) => setFilters((prev) => ({ ...prev, scope: event.target.value }))}
            >
              <option value="">All</option>
              <option value="global">Global</option>
              <option value="property">Selected properties</option>
              <option value="host">Host</option>
              <option value="property_type">Property type</option>
              <option value="provider">Provider</option>
              <option value="provider_property">Provider property</option>
              <option value="region">Region</option>
            </select>
          </Field>
          <Field label="Property">
            <select
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
              value={filters.property_id}
              onChange={(event) => setFilters((prev) => ({ ...prev, property_id: event.target.value }))}
            >
              <option value="">All</option>
              {propertyOptions.map((property) => (
                <option key={property.id} value={property.id}>
                  {property.title}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Provider code">
            <input
              type="text"
              list="provider-code-options"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
              placeholder="e.g. elivaas"
              value={filters.provider_code}
              onChange={(event) => setFilters((prev) => ({ ...prev, provider_code: event.target.value }))}
            />
          </Field>
          <Field label="Active">
            <select
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
              value={filters.active}
              onChange={(event) => setFilters((prev) => ({ ...prev, active: event.target.value }))}
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </Field>
          <Field label="Search label/name">
            <input
              type="text"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
              placeholder="Search rules"
              value={filters.q}
              onChange={(event) => setFilters((prev) => ({ ...prev, q: event.target.value }))}
            />
          </Field>
        </div>
        <datalist id="provider-code-options">
          <option value="elivaas" />
        </datalist>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Rules</h2>
            <p className="text-sm text-slate-500">
              {visibleRules.length} {visibleRules.length === 1 ? 'rule' : 'rules'} shown
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-6 py-3 font-semibold">Rule</th>
                <th className="px-6 py-3 font-semibold">Scope</th>
                <th className="px-6 py-3 font-semibold">Charge type</th>
                <th className="px-6 py-3 font-semibold">Applies on</th>
                <th className="px-6 py-3 font-semibold">Value</th>
                <th className="px-6 py-3 font-semibold">Sort order</th>
                <th className="px-6 py-3 font-semibold">Status</th>
                <th className="px-6 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-6 py-8 text-slate-500" colSpan={8}>
                    Loading rules...
                  </td>
                </tr>
              ) : visibleRules.length ? (
                visibleRules.map((rule) => (
                  <tr key={rule.id} className="border-t border-slate-100 text-slate-700">
                    <td className="px-6 py-4 align-top">
                      <div className="font-semibold text-slate-900">{rule.display_name}</div>
                      <div className="text-xs text-slate-500">{rule.rule_name}</div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="font-medium text-slate-900">{renderRuleScopeLabel(rule, propertyOptions).title}</div>
                      <div className="text-xs text-slate-500">{renderRuleScopeLabel(rule, propertyOptions).subtitle}</div>
                    </td>
                    <td className="px-6 py-4 align-top">{taxFeeChargeLabels[rule.charge_type] || rule.charge_type}</td>
                    <td className="px-6 py-4 align-top">{taxFeeAppliesLabels[rule.applies_on] || rule.applies_on}</td>
                    <td className="px-6 py-4 align-top">
                      {rule.charge_type === 'tax_percentage' || rule.charge_type === 'margin_percent'
                        ? `${rule.value}%`
                        : formatCurrency(rule.value)}
                    </td>
                    <td className="px-6 py-4 align-top">{rule.sort_order}</td>
                    <td className="px-6 py-4 align-top">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                          (rule.status ?? rule.is_active) ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {rule.status ?? rule.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(rule)}
                          className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:border-slate-300"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggle(rule)}
                          className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:border-slate-300"
                        >
                          {rule.status ?? rule.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(rule)}
                          className="rounded-lg border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="px-6 py-10 text-center text-slate-500" colSpan={8}>
                    No tax or fee rules match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Quote Preview</h2>
            <p className="text-sm text-slate-500">Test a property, stay window, and guest count before publishing.</p>
          </div>
          <button
            type="button"
            onClick={handlePreview}
            disabled={preview.loading}
            className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {preview.loading ? 'Previewing...' : 'Preview quote'}
          </button>
        </div>
        <div className="mt-5 grid gap-4 lg:grid-cols-4">
          <Field label="Property">
            <select
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
              value={previewParams.property_id}
              onChange={(event) => setPreviewParams((prev) => ({ ...prev, property_id: event.target.value }))}
            >
              <option value="">Select</option>
              {propertyOptions.map((property) => (
                <option key={property.id} value={property.id}>
                  {property.title}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Provider code">
            <input
              type="text"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
              placeholder="e.g. elivaas"
              value={previewParams.provider_code}
              onChange={(event) => setPreviewParams((prev) => ({ ...prev, provider_code: event.target.value }))}
            />
          </Field>
          <Field label="Provider property ID">
            <input
              type="text"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
              placeholder="Provider inventory ID"
              value={previewParams.provider_property_id}
              onChange={(event) => setPreviewParams((prev) => ({ ...prev, provider_property_id: event.target.value }))}
            />
          </Field>
          <Field label="Check-in">
            <input
              type="date"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
              value={previewParams.check_in}
              onChange={(event) => setPreviewParams((prev) => ({ ...prev, check_in: event.target.value }))}
            />
          </Field>
          <Field label="Check-out">
            <input
              type="date"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
              value={previewParams.check_out}
              onChange={(event) => setPreviewParams((prev) => ({ ...prev, check_out: event.target.value }))}
            />
          </Field>
          <Field label="Guests">
            <input
              type="number"
              min="1"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
              value={previewParams.guests_count}
              onChange={(event) => setPreviewParams((prev) => ({ ...prev, guests_count: event.target.value }))}
            />
          </Field>
        </div>
        <div className="mt-3 text-xs text-slate-500">
          Use either a direct property or a provider code + provider property ID. Provider fields override the property selector.
        </div>
        {preview.error ? (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {preview.error}
          </div>
        ) : null}
        {preview.data ? (
          <div className="mt-6 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Base Price</span>
                  <span className="font-semibold text-slate-900">
                    {formatCurrency(preview.data.base_price || preview.data.base_amount)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Discount</span>
                  <span className="font-semibold text-slate-900">
                    -{formatCurrency(preview.data.discount_amount || preview.data.discount_total || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Taxable subtotal</span>
                  <span className="font-semibold text-slate-900">
                    {formatCurrency(preview.data.taxable_subtotal || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">GST</span>
                  <span className="font-semibold text-slate-900">
                    {formatCurrency(preview.data.gst_amount || preview.data.tax_total || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Mandatory fees</span>
                  <span className="font-semibold text-slate-900">
                    {formatCurrency(preview.data.mandatory_fees || preview.data.fee_total || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-slate-200 pt-3 text-base">
                  <span className="font-semibold text-slate-900">Final payable amount</span>
                  <span className="font-semibold text-slate-900">
                    {formatCurrency(preview.data.final_payable || preview.data.grand_total)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Settlement to supplier</span>
                  <span className="font-semibold text-slate-900">
                    {formatCurrency(preview.data.settlement_to_supplier || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Homevilas revenue after discount</span>
                  <span className="font-semibold text-slate-900">
                    {formatCurrency(preview.data.homevilas_revenue_after_discount || 0)}
                  </span>
                </div>
              </div>
              <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500">Applied margin rule</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {preview.data.applied_margin_rule?.display_name || preview.data.applied_margin_rule?.rule_name || 'No margin rule applied'}
                    </div>
                  </div>
                  {preview.data.applied_margin_rule ? (
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                      {preview.data.applied_margin_rule.value}%
                    </span>
                  ) : null}
                </div>
                {preview.data.applied_margin_rule ? (
                  <div className="mt-2 text-xs text-slate-500">
                    {renderRuleScopeLabel(preview.data.applied_margin_rule, propertyOptions).title}: {' '}
                    {renderRuleScopeLabel(preview.data.applied_margin_rule, propertyOptions).subtitle}
                  </div>
                ) : (
                  <div className="mt-2 text-xs text-slate-500">
                    Create an active margin rule above if this quote should include a Homevilas markup.
                  </div>
                )}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-sm font-semibold text-slate-900">Applicable lines</div>
              <div className="mt-3 space-y-2 text-sm text-slate-700">
                {[...(preview.data.tax_fee_breakdown?.fee_lines || []), ...(preview.data.tax_fee_breakdown?.tax_lines || [])].length ? (
                  [...(preview.data.tax_fee_breakdown?.fee_lines || []), ...(preview.data.tax_fee_breakdown?.tax_lines || [])].map((line, index) => (
                    <div key={`${line.id || line.label || 'line'}-${index}`} className="flex items-center justify-between">
                      <span>{line.label || line.display_name || line.rule_name || 'Charge'}</span>
                      <span className="font-semibold text-slate-900">{formatCurrency(line.amount)}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-slate-500">No active tax or fee rule applies to this quote.</div>
                )}
              </div>
              {preview.data.partial_payment_enabled ? (
                <div className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-3 text-sm text-emerald-900">
                  <div className="flex items-center justify-between">
                    <span>Partial payment</span>
                    <span className="font-semibold">{preview.data.partial_percentage}%</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span>Pay now</span>
                    <span className="font-semibold">{formatCurrency(preview.data.partial_amount)}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span>Remaining after booking confirmation</span>
                    <span className="font-semibold">{formatCurrency(preview.data.remaining_amount)}</span>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {drawerOpen ? (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/35">
          <div className="h-full w-full max-w-xl overflow-y-auto bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  {editingRule ? 'Edit tax/fee rule' : 'Create tax/fee rule'}
                </h2>
                <p className="text-sm text-slate-500">
                  Configure how the rule is applied at checkout.
                </p>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:border-slate-300"
              >
                Close
              </button>
            </div>
            <div className="mt-6 grid gap-4">
              <Field label="Rule name">
                <input
                  type="text"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                  value={form.rule_name}
                  onChange={(event) => setForm((prev) => ({ ...prev, rule_name: event.target.value }))}
                />
              </Field>
              <Field label="Display name">
                <input
                  type="text"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                  value={form.display_name}
                  onChange={(event) => setForm((prev) => ({ ...prev, display_name: event.target.value }))}
                />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Scope">
                  <select
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                    value={form.scope_type}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        scope_type: event.target.value,
                        property_id: '',
                        host_id: '',
                        property_type: '',
                        region: '',
                        provider_code: '',
                        provider_property_id: '',
                      }))
                    }
                  >
                    <option value="global">Global</option>
                    <option value="property">Selected properties</option>
                    <option value="host">Host</option>
                    <option value="property_type">Property type</option>
                    <option value="provider">Provider</option>
                    <option value="provider_property">Provider property</option>
                    <option value="region">Region</option>
                  </select>
                </Field>
                <Field label="Property">
                  <select
                    disabled={form.scope_type !== 'property'}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm disabled:bg-slate-50"
                    value={form.property_id}
                    onChange={(event) => setForm((prev) => ({ ...prev, property_id: event.target.value }))}
                  >
                    <option value="">Select property</option>
                    {propertyOptions.map((property) => (
                      <option key={property.id} value={property.id}>
                        {property.title}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              {form.scope_type === 'host' ? (
                <Field label="Host ID">
                  <input
                    type="number"
                    min="1"
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                    value={form.host_id}
                    onChange={(event) => setForm((prev) => ({ ...prev, host_id: event.target.value }))}
                  />
                </Field>
              ) : null}
              {form.scope_type === 'property_type' ? (
                <Field label="Property type">
                  <input
                    type="text"
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                    value={form.property_type}
                    onChange={(event) => setForm((prev) => ({ ...prev, property_type: event.target.value }))}
                    placeholder="villa, apartment, cluster..."
                  />
                </Field>
              ) : null}
              {form.scope_type === 'region' ? (
                <Field label="Region">
                  <input
                    type="text"
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                    value={form.region}
                    onChange={(event) => setForm((prev) => ({ ...prev, region: event.target.value }))}
                    placeholder="state or region"
                  />
                </Field>
              ) : null}
              {form.scope_type === 'provider' || form.scope_type === 'provider_property' ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Provider code">
                    <input
                      type="text"
                      list="provider-code-options"
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                      value={form.provider_code}
                      onChange={(event) => setForm((prev) => ({ ...prev, provider_code: event.target.value }))}
                      placeholder="elivaas"
                    />
                  </Field>
                  <Field label="Provider property ID">
                    <input
                      type="text"
                      disabled={form.scope_type !== 'provider_property'}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm disabled:bg-slate-50"
                      value={form.provider_property_id}
                      onChange={(event) => setForm((prev) => ({ ...prev, provider_property_id: event.target.value }))}
                      placeholder="provider property id"
                    />
                  </Field>
                </div>
              ) : null}
              {form.scope_type === 'provider' || form.scope_type === 'provider_property' ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
                  Use <span className="font-semibold text-slate-900">provider code = elivaas</span> for Elivaas inventory.
                  Choose <span className="font-semibold text-slate-900">Margin / markup %</span> to control the Homevilas percentage.
                </div>
              ) : null}
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Charge type">
                  <select
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                    value={form.charge_type}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        charge_type: event.target.value,
                        applies_on: event.target.value === 'margin_percent' ? 'base_rent' : prev.applies_on,
                        calculation_target: event.target.value === 'margin_percent' ? 'customer' : prev.calculation_target,
                      }))
                    }
                  >
                    {Object.entries(taxFeeChargeLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={form.charge_type === 'margin_percent' ? 'Margin percentage (%)' : 'Value'}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                    value={form.value}
                    onChange={(event) => setForm((prev) => ({ ...prev, value: event.target.value }))}
                    placeholder={form.charge_type === 'margin_percent' ? 'e.g. 15' : ''}
                  />
                </Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Applies on">
                  <select
                    disabled={form.charge_type === 'margin_percent'}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                    value={form.applies_on}
                    onChange={(event) => setForm((prev) => ({ ...prev, applies_on: event.target.value }))}
                  >
                    {Object.entries(taxFeeAppliesLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Affects">
                  <select
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                    value={form.calculation_target}
                    onChange={(event) => setForm((prev) => ({ ...prev, calculation_target: event.target.value }))}
                  >
                    <option value="customer">Customer quote only</option>
                    <option value="both">Customer + settlement</option>
                    <option value="settlement">Settlement only</option>
                  </select>
                </Field>
              </div>
              {form.charge_type === 'margin_percent' ? (
                <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-xs text-emerald-900">
                  Margin rules do not stack. The most specific active scope wins, then the lowest sort order is applied to the quote.
                </div>
              ) : null}
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Sort order">
                  <input
                    type="number"
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                    value={form.sort_order}
                    onChange={(event) => setForm((prev) => ({ ...prev, sort_order: event.target.value }))}
                  />
                </Field>
              </div>
              <label className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={Boolean(form.status)}
                  onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.checked }))}
                />
                Active rule
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDrawer}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:border-slate-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                {saving ? 'Saving...' : editingRule ? 'Save changes' : 'Create rule'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const AdminSeasonalPricing = () => <AdminPricingRules mode="seasonal" />;
export const AdminSpecialOffers = () => <AdminPricingRules mode="offers" />;
export const AdminTaxesFees = () => <AdminTaxesFeeManager />;

export default AdminPricingRules;
